import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH, faEdit, faTrash, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import ReactDOM from 'react-dom';
import { useGroup } from '../../context/GroupContext';
import { useAuth } from '../../context/AuthContext';
import groupService from '../../api/groupService';
import { getTagConfig, getTagGradient, getTagIcon } from '../../utils/tagDefaults';
import '../../styles/group/CommunityPostCard.css';

// Component to handle rich text formatting
const FormattedText = ({ content }) => {
  if (!content) return null;
  
  // Split by lines and process each line
  const lines = content.split('\n');
  
  return (
    <>
      {lines.map((line, index) => {
        // Handle empty lines
        if (!line.trim()) {
          return <br key={index} />;
        }
        
        // Check for bullet points (•, -, *, or numbered lists)
        const bulletMatch = line.match(/^[\s]*[•·\-\*]\s*(.+)$/);
        const numberedMatch = line.match(/^[\s]*(\d+\.)\s*(.+)$/);
        
        if (bulletMatch) {
          return (
            <div key={index} className="formatted-bullet-point">
              <span className="bullet-symbol">•</span>
              <span className="bullet-text">{bulletMatch[1]}</span>
            </div>
          );
        }
        
        if (numberedMatch) {
          return (
            <div key={index} className="formatted-numbered-point">
              <span className="number-symbol">{numberedMatch[1]}</span>
              <span className="numbered-text">{numberedMatch[2]}</span>
            </div>
          );
        }
        
        // Regular text line
        return (
          <div key={index} className="formatted-text-line">
            {line}
          </div>
        );
      })}
    </>
  );
};

const CommunityPostCard = ({ post, groupId, onPostDeleted, onPostEdit }) => {
  const { currentUser } = useAuth();
  const { adminGroup } = useGroup();
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const menuRef = useRef(null);
  const deleteModalRef = useRef(null);

  // Check if current user can edit/delete this post
  const canModifyPost = currentUser && (currentUser.role === 'admin' || currentUser.id === post.author_id);

  // Constants for text truncation
  const DESCRIPTION_LIMIT = 150;
  const shouldShowSeeMore = post.content && post.content.length > DESCRIPTION_LIMIT;

  // Handle image URL processing
  const processImageUrl = (url) => {
    if (!url) return null;
    
    // If it's already a full URL, return as is
    if (url.startsWith('http')) {
      return url;
    }
    
    // If it's a relative path from Supabase storage, construct full URL
    if (url.startsWith('community-post-images/')) {
      return `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/${url}`;
    }
    
    return url;
  };

  // Handle menu toggle
  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  // Handle edit post
  const handleEdit = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onPostEdit) {
      onPostEdit(post);
    }
  };

  // Handle delete post
  const openDeleteModal = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const confirmDelete = async () => {
    try {
      await groupService.deleteCommunityPost(post.id);
      closeDeleteModal();
      if (onPostDeleted) {
        onPostDeleted(post.id);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Failed to delete post. Please try again.');
    }
  };

  // Handle click outside for menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu || showDeleteModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu, showDeleteModal]);

  // Handle image error
  const handleImageError = () => {
    console.error(`Failed to load image for post ${post.id}:`, {
      originalUrl: post.image_url,
      processedUrl: processImageUrl(post.image_url)
    });
    setImageError(true);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours === 0) {
        const diffInMins = Math.floor(diffInMs / (1000 * 60));
        return diffInMins < 1 ? 'Just now' : `${diffInMins}m ago`;
      }
      return `${diffInHours}h ago`;
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  // Get image URL - either user uploaded or default based on tag
  const getImageUrl = useCallback(() => {
    if (post.image_url && !imageError) {
      return post.image_url;
    }
    
    // Use default gradient background if no user image
    return null;
  }, [post.image_url, imageError]);

  // Format tag display
  const formatTagDisplay = (tag) => {
    if (!tag) return 'Post';
    return tag;
  };

  // Get group logo with fallback
  const getGroupLogoUrl = () => {
    console.log('Admin group data:', adminGroup);
    console.log('Logo URL field:', adminGroup?.logo_url);
    console.log('Legacy logo field:', adminGroup?.logo);
    
    if (adminGroup?.logo_url) {
      return processImageUrl(adminGroup.logo_url);
    }
    // Check for legacy logo field as fallback
    if (adminGroup?.logo) {
      return processImageUrl(adminGroup.logo);
    }
    // Return null to show fallback
    return null;
  };

  // Toggle expanded state
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Get display text for description with rich text support
  const getDisplayText = () => {
    if (!post.content) return '';
    if (!shouldShowSeeMore || isExpanded) {
      return post.content;
    }
    return `${post.content.substring(0, DESCRIPTION_LIMIT)}...`;
  };

  // Get formatted content for rich text display
  const getFormattedContent = () => {
    const textToDisplay = getDisplayText();
    return <FormattedText content={textToDisplay} />;
  };

  return (
    <div className="community-post-card">
      {/* Group info header - always at top */}
      <div className="community-post-card-header">
        <div className="post-group-info">
          <div className="post-group-logo-container">
            {getGroupLogoUrl() ? (
              <img 
                src={getGroupLogoUrl()} 
                alt={`${adminGroup?.name || 'Community'} logo`} 
                className="post-group-logo"
              />
            ) : (
              <div className="post-group-logo-fallback">
                {(adminGroup?.name || 'Community').charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="post-group-details">
            <span className="post-group-name">{adminGroup?.name || 'Community'}</span>
            <span className="post-date">{formatDate(post.created_at)}</span>
          </div>
        </div>
        
        {/* Action menu */}
        {canModifyPost && (
          <div className="community-post-card-menu-container" ref={menuRef}>
            <button 
              className="community-post-card-menu-button"
              onClick={handleMenuToggle}
              aria-label="Post options"
            >
              <FontAwesomeIcon icon={faEllipsisH} />
            </button>
            
            {showMenu && (
              <div className="community-post-action-menu">
                <button className="community-post-action-item edit" onClick={handleEdit}>
                  <FontAwesomeIcon icon={faEdit} /> Edit
                </button>
                <button className="community-post-action-item delete" onClick={openDeleteModal}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image container - always show (user image or default) */}
      <div className="community-post-card-image-container">
        {getImageUrl() ? (
          <img
            src={getImageUrl()}
            alt="Post content"
            className="community-post-card-img"
            onError={handleImageError}
          />
        ) : (
          <div 
            className="community-post-card-default-bg"
            style={{ 
              background: getTagGradient(post.tag) || 'linear-gradient(135deg, #ff0000 0%, #00ff00 100%)', // Fallback bright colors for testing
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column'
            }}
          >
            {/* Instagram-style text overlay */}
            <div className="instagram-style-text">
              <div className="main-text">
                {formatTagDisplay(post.tag)}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Content section - at bottom */}
      <div className="community-post-card-content">
        {/* Post title */}
        {post.title && (
          <h3 className="community-post-card-title">{post.title}</h3>
        )}
        
        {/* Post content */}
        <div className="community-post-card-description-container">
          <div className="community-post-card-description">
            {getFormattedContent()}
          </div>
          
          {/* See more/See less button */}
          {shouldShowSeeMore && (
            <button 
              className="see-more-button"
              onClick={toggleExpanded}
            >
              {isExpanded ? 'See less' : 'See more'}
            </button>
          )}
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && ReactDOM.createPortal(
        <div className="delete-modal-overlay" onClick={closeDeleteModal}>
          <div className="delete-modal" ref={deleteModalRef} onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <FontAwesomeIcon icon={faExclamationTriangle} className="warning-icon" />
              <h3>Delete Post</h3>
            </div>
            <div className="delete-modal-content">
              <p>Are you sure you want to delete this post?</p>
              <p className="delete-warning">This action cannot be undone.</p>
              
              <div className="delete-modal-actions">
                <button className="btn-cancel" onClick={closeDeleteModal}>
                  Cancel
                </button>
                <button className="btn-delete" onClick={confirmDelete}>
                  Delete Post
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CommunityPostCard;
